// Opens the ZenDesk help center in a new tab with Sierra auto-opened on arrival.

import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { submitZendeskSsoForm } from '../../services/zendesk-sso-service';
import { getPreferredLocale } from '../../intl/get-preferred-locale';
import { Segment } from '../../services/analytics-services/segment';
import { HelpCenterService } from '../../services/helpcenter-service';
import { CookieService } from '../../services/cookie-service';
import { ShippyEvents } from '../../tracking/tracking-constants';

export const OpenSierraSource = {
  FtuWizardHelp: 'ftu_wizard_help',
  HelpMenuChat: 'help_menu_chat',
  StoreErrorModal: 'store_error_modal',
  WalletApprovalNotification: 'wallet_approval_notification',
  WalletError: 'wallet_error',
} as const;

export type OpenSierraSource = (typeof OpenSierraSource)[keyof typeof OpenSierraSource];

/** Grabbag of variables auctane-sierra.js reads out of the handoff cookie and spreads into Sierra's config. */
export type SierraHandoffVariables = Record<string, string>;

const OPEN_CHAT_PARAM = 'open-chat=true';
const HANDOFF_VARIABLES_COOKIE = 'sierraHandoffVariables';
const HANDOFF_VARIABLES_COOKIE_EXPIRY_MINUTES = 30;

// Route through the SSO POST so users arrive authenticated — Sierra won't load without a Zendesk session.
export const useOpenSierraInZendesk = (): ((source: OpenSierraSource, handoffVariables?: SierraHandoffVariables) => Promise<void>) => {
  const locale = useSelector(getPreferredLocale);

  return useCallback(
    async (source: OpenSierraSource, handoffVariables?: SierraHandoffVariables) => {
      if (handoffVariables && Object.keys(handoffVariables).length > 0) {
        CookieService.setCookieValueWithExpirationInMinutes(
          HANDOFF_VARIABLES_COOKIE,
          JSON.stringify(handoffVariables),
          HANDOFF_VARIABLES_COOKIE_EXPIRY_MINUTES,
        );
      }

      const redirectTo = `${HelpCenterService.getHelpCenterUrlByLocale(locale)}?${OPEN_CHAT_PARAM}`;
      const didOpen = await submitZendeskSsoForm(HelpCenterService.getSignInRedirectUrl(redirectTo));
      if (didOpen) {
        Segment.trackEvent(ShippyEvents.SIERRA_HELP_CENTER_OPENED, { source });
      }
    },
    [locale],
  );
};
